/* eslint-disable */
import React, { useRef, useState, useEffect } from 'react';
import { useLadderEditorStore } from './store/ladderEditorStore';
import { useProjectStore } from '../../../store/projectStore';
import { useUiStore } from '../../../store/uiStore';
import { useSimulationStore } from '../../../store/simulationStore';
import type { LadderNetwork, LadderProgram } from '../../../ladder/model/LadderProgram';
import type { LadderElementType, LadderElement } from '../../../ladder/model/LadderElement';
import styles from './LadderCanvas.module.css';
import { formatTime } from '../../../plc/core/timeParser';
import { parseAddress } from '../../../plc/address/parser';
import { isTextInputTarget } from '@/app/shortcuts/shortcuts';
import { useT } from '@/i18n/useT';

const CELL_W = 120;
const CELL_H = 80;
const COLS = 12;
const HEADER_H = 40;
const NETWORK_GAP = 60; // Extra breathing space between networks so multi-row elements do not overlap

function getNetRows(net: LadderNetwork): number {
  const elements = net?.elements ? Object.values(net.elements) : [];
  if (elements.length === 0) return 1;
  return Math.max(1, ...elements.map((e: any) => {
    const isMultiRow = e.type === 'CTU' || e.type === 'CTD' || e.type === 'CTUD' || e.type === 'SR' || e.type === 'RS' || e.type === 'TONR' || e.type === 'V_LINE';
    return e.row + (isMultiRow ? 2 : 1);
  }));
}

function getNextAddress(program: any, prefix: 'I' | 'Q' | 'T' | 'C'): string {
  let maxByte = -1;
  let maxBit = -1;
  let maxTC = -1;

  for (const net of program.networks) {
    for (const el of Object.values(net.elements) as any[]) {
      if (el.address && typeof el.address === 'string') {
        const parsed = parseAddress(el.address);
        if (parsed && parsed.area === prefix) {
          if (prefix === 'I' || prefix === 'Q') {
            if (parsed.byteOffset > maxByte) {
              maxByte = parsed.byteOffset;
              maxBit = parsed.bitOffset;
            } else if (parsed.byteOffset === maxByte && parsed.bitOffset > maxBit) {
              maxBit = parsed.bitOffset;
            }
          } else {
            if (parsed.byteOffset > maxTC) {
              maxTC = parsed.byteOffset;
            }
          }
        }
      }
    }
  }

  if (prefix === 'I' || prefix === 'Q') {
    if (maxByte === -1) return `${prefix}0.0`;
    let nextBit = maxBit + 1;
    let nextByte = maxByte;
    if (nextBit > 7) {
      nextBit = 0;
      nextByte++;
    }
    return `${prefix}${nextByte}.${nextBit}`;
  } else {
    if (maxTC === -1) return `${prefix}1`;
    return `${prefix}${maxTC + 1}`;
  }
}

export function isRightAligned(type: string): boolean {
  return type === 'COIL' || 
         type === 'SET_COIL' || 
         type === 'RESET_COIL' || 
         type === 'INVERT_COIL' || 
         type === 'JMP' || 
         type === 'LBL';
}

export function cleanOrphanedBranchLines(networks: LadderNetwork[] = []): { networks: LadderNetwork[]; changed: boolean } {
  if (!Array.isArray(networks)) return { networks: [], changed: false };
  let anyChanged = false;
  const cleanedNetworks = networks.map(n => {
    let netChanged = false;
    const elements = { ...(n?.elements || {}) };
    
    // Ensure all coils / right-aligned elements always sit at COLS - 1 in data
    Object.values(elements).forEach(el => {
      if (isRightAligned(el.type) && el.column !== COLS - 1) {
        elements[el.id] = { ...el, column: COLS - 1 };
        netChanged = true;
        anyChanged = true;
      }
    });

    // Check for orphaned horizontal lines at row > 0, column 0:
    // A horizontal line at (row > 0, col 0) is ONLY valid if there is a vertical connection (V_LINE)
    // at column 0 touching this row (i.e. row - 1 or row).
    // Without a V_LINE at column 0, power cannot flow to (row > 0, col 0) from the power rail!
    Object.values(elements).forEach(el => {
      if (el.type === 'H_LINE' && el.row > 0 && el.column === 0) {
        const hasVLineAtZero = Object.values(elements).some(
          v => v.type === 'V_LINE' && v.column === 0 && (v.row === el.row - 1 || v.row === el.row)
        );
        if (!hasVLineAtZero) {
          delete elements[el.id];
          netChanged = true;
          anyChanged = true;
        }
      }
    });

    return netChanged ? { ...n, elements } : n;
  });

  return { networks: cleanedNetworks, changed: anyChanged };
}

export function getAutoFillLines(
  elements: Record<string, LadderElement>,
  targetRow: number,
  targetCol: number
): LadderElement[] {
  if (targetCol <= 0) return [];
  // Sub-branches (targetRow > 0) must NEVER auto-fill column 0
  if (targetRow > 0 && targetCol <= 1) return [];

  let fillStart = -1;

  if (targetRow === 0) {
    // Row 0 is the main rung originating from the left power rail at column 0.
    let maxColBefore = -1;
    Object.values(elements).forEach(e => {
      if (e.row === 0 && e.column < targetCol && e.type !== 'V_LINE') {
        if (e.column > maxColBefore) maxColBefore = e.column;
      }
    });
    fillStart = maxColBefore >= 0 ? maxColBefore + 1 : 0;
  } else {
    // Row > 0 is a sub-branch. It must NEVER connect directly to the left rail (column 0).
    // It only connects to elements on the same row, or to a vertical branch junction (V_LINE).
    let maxColBefore = -1;
    Object.values(elements).forEach(e => {
      if (e.row === targetRow && e.column < targetCol && e.type !== 'V_LINE') {
        if (e.column > maxColBefore) maxColBefore = e.column;
      }
    });

    if (maxColBefore >= 0) {
      fillStart = maxColBefore + 1;
    } else {
      // Find any vertical branch (V_LINE) connecting to targetRow at or before targetCol
      let maxVCol = -1;
      Object.values(elements).forEach(e => {
        if (e.type === 'V_LINE' && (e.row === targetRow - 1 || e.row === targetRow) && e.column <= targetCol) {
          if (e.column > maxVCol) maxVCol = e.column;
        }
      });

      if (maxVCol >= 0) {
        if (maxVCol === targetCol) {
          // Element is placed right at the branch junction (V_LINE). No fill lines needed!
          fillStart = targetCol;
        } else {
          // Branch junction is at maxVCol (< targetCol). Fill from the junction to targetCol.
          fillStart = maxVCol;
        }
      } else {
        // No element and no branch junction to the left. Do NOT fill from column 0!
        fillStart = targetCol;
      }
    }

    // Safety constraint: Never auto-fill column 0 on a sub-branch!
    fillStart = Math.max(1, fillStart);
  }

  const lines: LadderElement[] = [];
  for (let c = fillStart; c < targetCol; c++) {
    lines.push({
      id: `el_${Date.now()}_hline_${c}_${Math.floor(Math.random() * 1000)}`,
      type: 'H_LINE',
      row: targetRow,
      column: c
    });
  }
  return lines;
}

export function deleteLadderElement(
  program: LadderProgram,
  elementId: string,
  key: 'Delete' | 'Backspace' = 'Delete'
): { program: LadderProgram; nextSelectedId: string | null } {
  let targetEl: LadderElement | undefined;
  let targetNet: LadderNetwork | undefined;

  for (const n of program.networks) {
    if (n.elements[elementId]) {
      targetEl = n.elements[elementId];
      targetNet = n;
      break;
    }
  }

  if (!targetEl || !targetNet) {
    return { program, nextSelectedId: null };
  }

  let nextSelectedId: string | null = null;
  const newNetworks = program.networks.map(n => {
    if (n.id !== targetNet!.id) return n;
    const newEls = { ...n.elements };
    delete newEls[elementId];

    // Delete key: If element was NOT H_LINE and NOT V_LINE, replace it with H_LINE to preserve continuity
    if (key === 'Delete' && targetEl!.type !== 'H_LINE' && targetEl!.type !== 'V_LINE') {
      const newLineId = `el_${Date.now()}_hline_${targetEl!.row}_${targetEl!.column}_${Math.floor(Math.random() * 1000)}`;
      newEls[newLineId] = {
        id: newLineId,
        type: 'H_LINE',
        row: targetEl!.row,
        column: targetEl!.column
      };
      nextSelectedId = newLineId;
    }

    // Backspace key: completely delete without creating H_LINE, select preceding element to the left (column < targetEl.column)
    if (key === 'Backspace') {
      const candidates = Object.values(newEls).filter(e => 
        e.row === targetEl!.row && 
        e.column < targetEl!.column && 
        e.type !== 'V_LINE'
      ).sort((a, b) => b.column - a.column);

      if (candidates.length > 0) {
        nextSelectedId = candidates[0]!.id;
      }
    }

    return { ...n, elements: newEls };
  });

  const { networks: cleanedNetworks } = cleanOrphanedBranchLines(newNetworks);
  return {
    program: { ...program, networks: cleanedNetworks },
    nextSelectedId
  };
}

export function deleteMultipleLadderElements(
  program: LadderProgram,
  elementIds: string[],
  key: 'Delete' | 'Backspace' = 'Delete'
): LadderProgram {
  if (elementIds.length === 0) return program;
  const newNetworks = program.networks.map(n => {
    let changed = false;
    const newEls = { ...n.elements };
    for (const id of elementIds) {
      if (newEls[id]) {
        changed = true;
        const el = newEls[id]!;
        delete newEls[id];
        // Only replace with H_LINE on 'Delete' key, NOT on 'Backspace'
        if (key === 'Delete' && el.type !== 'H_LINE' && el.type !== 'V_LINE') {
          const newLineId = `el_${Date.now()}_hline_${el.row}_${el.column}_${Math.floor(Math.random() * 1000)}`;
          newEls[newLineId] = {
            id: newLineId,
            type: 'H_LINE',
            row: el.row,
            column: el.column
          };
        }
      }
    }
    return changed ? { ...n, elements: newEls } : n;
  });

  const { networks: cleanedNetworks } = cleanOrphanedBranchLines(newNetworks);
  return { ...program, networks: cleanedNetworks };
}

export function insertLadderElement(
  program: LadderProgram,
  netId: string,
  row: number,
  col: number,
  type: LadderElementType
): { program: LadderProgram; placedElementId: string } {
  const isCoil = isRightAligned(type);
  const finalCol = isCoil ? COLS - 1 : col;
  let newElements: LadderElement[] = [];

  if ((type as string) === 'BRANCH') {
    const id1 = `el_${Date.now()}_1`;
    const id2 = `el_${Date.now()}_2`;
    const id3 = `el_${Date.now()}_3`;
    newElements = [
      { id: id1, type: 'V_LINE', row, column: finalCol },
      { id: id2, type: 'H_LINE', row: row + 1, column: finalCol },
      { id: id3, type: 'V_LINE', row, column: finalCol + 1 }
    ];
  } else {
    const elementId = `el_${Date.now()}_${Math.floor(Math.random() * 1000)}`;
    const newElement: any = {
      id: elementId,
      type: type as any,
      row,
      column: finalCol
    };

    if (type === 'NO_CONTACT' || type === 'NC_CONTACT' || type === 'P_CONTACT' || type === 'N_CONTACT') {
      newElement.address = getNextAddress(program, 'I');
    } else if (type === 'COIL' || type === 'SET_COIL' || type === 'RESET_COIL' || type === 'INVERT_COIL') {
      newElement.address = getNextAddress(program, 'Q');
    } else if (type === 'SR' || type === 'RS') {
      newElement.address = 'M0.0';
    } else if (type === 'TON' || type === 'TOF' || type === 'TP' || type === 'TONR') {
      newElement.address = getNextAddress(program, 'T');
      newElement.presetTime = '5s';
    } else if (type === 'CTU' || type === 'CTD' || type === 'CTUD') {
      newElement.address = getNextAddress(program, 'C');
      newElement.presetValue = '5';
    } else if (['CMP_EQ_I', 'CMP_NE_I', 'CMP_GT_I', 'CMP_LT_I', 'CMP_GE_I', 'CMP_LE_I'].includes(type)) {
      newElement.in1 = 'MW0';
      newElement.in2 = '0';
    } else if (['CMP_EQ_R', 'CMP_NE_R', 'CMP_GT_R', 'CMP_LT_R', 'CMP_GE_R', 'CMP_LE_R'].includes(type)) {
      newElement.in1 = 'MD0';
      newElement.in2 = '0.0';
    } else if (['ADD_I', 'SUB_I', 'MUL_I', 'DIV_I'].includes(type)) {
      newElement.in1 = 'MW0';
      newElement.in2 = '1';
      newElement.out = 'MW2';
    } else if (['ADD_R', 'SUB_R', 'MUL_R', 'DIV_R'].includes(type)) {
      newElement.in1 = 'MD0';
      newElement.in2 = '1.0';
      newElement.out = 'MD4';
    } else if (type === 'MOV_W') {
      newElement.in = 'MW0';
      newElement.out = 'MW2';
    } else if (type === 'MOV_R') {
      newElement.in = 'MD0';
      newElement.out = 'MD4';
    } else if (type === 'SCALE') {
      newElement.in = 'IW0';
      newElement.inMin = '0';
      newElement.inMax = '27648';
      newElement.outMin = '0.0';
      newElement.outMax = '100.0';
      newElement.out = 'MD0';
    } else if (type === 'IN_RANGE') {
      newElement.in = 'MW0';
      newElement.min = '10';
      newElement.max = '50';
    } else if (type === 'JMP' || type === 'LBL') {
      newElement.label = 'LAB1';
    }
    newElements.push(newElement as LadderElement);
  }

  // Auto-fill left gap with H_LINEs for functional elements (not when placing H_LINE or V_LINE)
  const targetNet = program.networks.find(n => n.id === netId);
  const autoFillLines = (targetNet && type !== 'H_LINE' && type !== 'V_LINE')
    ? getAutoFillLines(targetNet.elements, row, finalCol)
    : [];
  newElements.push(...autoFillLines);

  const newProgram = {
    ...program,
    networks: program.networks.map(n => {
      if (n.id !== netId) return n;
      let newEls = { ...n.elements };

      newElements.forEach(el => {
        const isV = el.type === 'V_LINE';
        Object.values(newEls).forEach(existing => {
          if (existing.row === el.row && existing.column === el.column) {
            // Placing an H_LINE or V_LINE must NEVER delete a coil or right-aligned relay
            if ((el.type === 'H_LINE' || el.type === 'V_LINE') && isRightAligned(existing.type)) {
              return;
            }
            const existingIsV = existing.type === 'V_LINE';
            // Requirement 2: Delete underlying H_LINE or matching element
            if (isV === existingIsV || existing.type === 'H_LINE' || (el.type !== 'V_LINE' && existing.type !== 'V_LINE')) {
              delete newEls[existing.id];
            }
          }
          // Multi-row blocks also delete underlying H_LINE on lower row
          if (['CTU', 'CTD', 'CTUD', 'SR', 'RS'].includes(el.type) && existing.row === el.row + 1 && existing.column === el.column && existing.type === 'H_LINE') {
            delete newEls[existing.id];
          }
        });
        newEls[el.id] = el;
      });

      return { ...n, elements: newEls };
    })
  };

  const { networks: cleanedNetworks } = cleanOrphanedBranchLines(newProgram.networks);
  return {
    program: { ...newProgram, networks: cleanedNetworks },
    placedElementId: newElements[0]?.id || ''
  };
}

export function drawLadderHLineSeries(
  program: LadderProgram,
  netId: string,
  row: number,
  startCol: number,
  endCol: number
): LadderProgram {
  const minCol = Math.max(0, Math.min(startCol, endCol));
  const maxCol = Math.min(COLS - 1, Math.max(startCol, endCol));

  const newNetworks = program.networks.map(n => {
    if (n.id !== netId) return n;
    const newEls = { ...n.elements };
    let changed = false;

    for (let c = minCol; c <= maxCol; c++) {
      const existing = Object.values(newEls).find(e => e.row === row && e.column === c && e.type !== 'V_LINE');
      if (!existing || (isRightAligned(existing.type) && c < COLS - 1)) {
        if (existing && isRightAligned(existing.type) && c < COLS - 1) {
          newEls[existing.id] = { ...existing, column: COLS - 1 };
        }
        const newLineId = `el_${Date.now()}_hline_${row}_${c}_${Math.floor(Math.random() * 1000)}`;
        newEls[newLineId] = {
          id: newLineId,
          type: 'H_LINE',
          row,
          column: c
        };
        changed = true;
      }
    }

    return changed ? { ...n, elements: newEls } : n;
  });

  return { ...program, networks: newNetworks };
}

export function LadderCanvas() {
  const containerRef = useRef<HTMLDivElement>(null);
  const svgRef = useRef<SVGSVGElement>(null);
  const t = useT();
  const { 
    zoom, panX, panY, setZoom, setPan, 
    executeCommand, undo, redo,
    selectedElementIds, toggleSelectedElementId, setSelectedElementIds, clearSelection: clearEditorSelection,
    activeTool, setActiveTool,
    clipboard, setClipboard
  } = useLadderEditorStore();
  const program = useProjectStore(s => s.project?.plc?.program ?? { networks: [] });
  const { select, clearSelection: clearGlobalSelection } = useUiStore();
  const panelSizes = useUiStore((s) => s.panelSizes);
  const leftPanelVisible = useUiStore((s) => s.leftPanelVisible);
  const rightPanelVisible = useUiStore((s) => s.rightPanelVisible);
  const simState = useSimulationStore(s => s.snapshot);
  const isSimulationRunning = simState.state !== 'STOPPED';

  const [containerWidth, setContainerWidth] = useState<number>(0);

  // Measure container width and observe resizing (e.g. sidebar dragging)
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const update = () => {
      if (container.clientWidth > 0) {
        setContainerWidth(container.clientWidth);
      }
    };

    update();

    const ro = new ResizeObserver((entries) => {
      for (const entry of entries) {
        if (entry.contentRect.width > 0) {
          setContainerWidth(entry.contentRect.width);
        }
      }
    });

    ro.observe(container);
    return () => ro.disconnect();
  }, []);

  // Also trigger update when panel sizes or visibility change
  useEffect(() => {
    if (containerRef.current && containerRef.current.clientWidth > 0) {
      setContainerWidth(containerRef.current.clientWidth);
    }
  }, [panelSizes.left, panelSizes.right, leftPanelVisible, rightPanelVisible]);

  // Dynamic cell width based on available canvas width
  // 40px left padding for power rail, 60px right padding for delete button & margin
  const cellWidth = containerWidth > 0 
    ? Math.max(60, (containerWidth - 100) / COLS) 
    : CELL_W;
  const netWidth = COLS * cellWidth;

  useEffect(() => {
    if (isSimulationRunning) {
      clearEditorSelection();
      clearGlobalSelection();
      setActiveTool(null);
      setDraggingElement(null);
      setHoverCell(null);
      setSelectionRect(null);
      isDrawingLine.current = false;
      lastDrawnCell.current = null;
    }
  }, [isSimulationRunning]);

  const [hoverCell, setHoverCell] = useState<{ netId: string, row: number, col: number } | null>(null);
  const [draggingElement, setDraggingElement] = useState<{ id: string, netId: string } | null>(null);

  // Pan logic
  const [isPanning, setIsPanning] = useState(false);
  const lastMouse = useRef({ x: 0, y: 0 });
  
  // Selection rectangle
  const [selectionRect, setSelectionRect] = useState<{ startX: number, startY: number, currentX: number, currentY: number } | null>(null);
  const isSpacePressed = useRef(false);

  // Serial line drawing refs
  const isDrawingLine = useRef(false);
  const lastDrawnCell = useRef<{ netId: string; row: number; col: number } | null>(null);
  const strokeProgram = useRef<LadderProgram | null>(null);

  const placeElementAt = (netId: string, row: number, col: number, type: LadderElementType) => {
    const { program: newProgram, placedElementId } = insertLadderElement(program, netId, row, col, type);
    executeCommand(newProgram);
    if (placedElementId) {
      setSelectedElementIds([placedElementId]);
      select(placedElementId, 'ladderElement');
    }
  };

  const handleDeleteKey = (key: 'Delete' | 'Backspace') => {
    const currentSelectedIds = selectedElementIds.length > 0 
      ? selectedElementIds 
      : (useUiStore.getState().selectedObjectType === 'ladderElement' && useUiStore.getState().selectedObjectId)
        ? [useUiStore.getState().selectedObjectId!]
        : [];

    if (currentSelectedIds.length === 0) return;

    if (currentSelectedIds.length === 1) {
      const { program: newProgram, nextSelectedId } = deleteLadderElement(program, currentSelectedIds[0]!, key);
      executeCommand(newProgram);
      if (nextSelectedId) {
        setSelectedElementIds([nextSelectedId]);
        select(nextSelectedId, 'ladderElement');
      } else {
        clearEditorSelection();
        clearGlobalSelection();
      }
    } else {
      const newProgram = deleteMultipleLadderElements(program, currentSelectedIds, key);
      executeCommand(newProgram);
      clearEditorSelection();
      clearGlobalSelection();
    }
  };

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (isTextInputTarget(e.target) || (document.activeElement && ['INPUT', 'TEXTAREA', 'SELECT'].includes(document.activeElement.tagName))) {
        return;
      }
      if (e.code === 'Space') {
        isSpacePressed.current = true;
        if (svgRef.current) svgRef.current.style.cursor = 'grab';
      }
    };
    const handleKeyUp = (e: KeyboardEvent) => {
      if (e.code === 'Space') {
        isSpacePressed.current = false;
        if (svgRef.current) svgRef.current.style.cursor = 'default';
        setIsPanning(false);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
    };
  }, []);

  // Set initial 1:1 view on load
  useEffect(() => {
    setZoom(1);
    setPan(0, 0);
  }, []);

  // Ensure panY stays clamped to <= 0 so Network 1 is anchored at the top
  useEffect(() => {
    if (panY > 0) {
      setPan(panX, 0);
    }
  }, [panY, panX, setPan]);

  // Auto-clean any orphaned ghost lines on sub-branches (e.g. H_LINE at row > 0, col 0 without V_LINE)
  useEffect(() => {
    if (isSimulationRunning || !program?.networks) return;
    const { networks: cleaned, changed } = cleanOrphanedBranchLines(program.networks);
    if (changed) {
      useProjectStore.getState().setProgram({ ...program, networks: cleaned });
    }
  }, [program, isSimulationRunning]);

  const handlePointerDown = (e: React.PointerEvent) => {
    if (e.button === 1 || isSpacePressed.current) {
      setIsPanning(true);
      lastMouse.current = { x: e.clientX, y: e.clientY };
      e.currentTarget.setPointerCapture(e.pointerId);
      if (svgRef.current) svgRef.current.style.cursor = 'grabbing';
      return;
    }
    
    if (isSimulationRunning) {
      return;
    }

    // Left click
    if (e.button === 0 && !draggingElement) {
      if (activeTool === 'H_LINE') {
        const coords = getGridCoords(e.clientX, e.clientY);
        if (coords) {
          isDrawingLine.current = true;
          lastDrawnCell.current = coords;
          strokeProgram.current = useProjectStore.getState().project.plc.program;
          e.currentTarget.setPointerCapture(e.pointerId);

          const updated = drawLadderHLineSeries(strokeProgram.current, coords.netId, coords.row, coords.col, coords.col);
          useProjectStore.getState().setProgram(updated);
          return;
        }
      } else if (activeTool) {
        const coords = getGridCoords(e.clientX, e.clientY);
        if (coords) {
          placeElementAt(coords.netId, coords.row, coords.col, activeTool as LadderElementType);
          return;
        }
      }

      const targetTag = (e.target as Element).tagName.toLowerCase();
      // Only start selection rect if clicking on the background (svg or div), not on an element
      if (targetTag === 'svg' || targetTag === 'div') {
        setSelectionRect({
          startX: e.clientX,
          startY: e.clientY,
          currentX: e.clientX,
          currentY: e.clientY
        });
        e.currentTarget.setPointerCapture(e.pointerId);
      }
    }
  };

  const handlePointerMove = (e: React.PointerEvent) => {
    if (isPanning) {
      const dx = e.clientX - lastMouse.current.x;
      const dy = e.clientY - lastMouse.current.y;
      setPan(panX + dx, Math.min(0, panY + dy));
      lastMouse.current = { x: e.clientX, y: e.clientY };
    } else if (isSimulationRunning) {
      if (hoverCell) setHoverCell(null);
    } else if (isDrawingLine.current && lastDrawnCell.current) {
      const coords = getGridCoords(e.clientX, e.clientY);
      if (coords && (coords.netId !== lastDrawnCell.current.netId || coords.row !== lastDrawnCell.current.row || coords.col !== lastDrawnCell.current.col)) {
        const currentProg = useProjectStore.getState().project.plc.program;
        if (coords.netId === lastDrawnCell.current.netId && coords.row === lastDrawnCell.current.row) {
          const updated = drawLadderHLineSeries(currentProg, coords.netId, coords.row, lastDrawnCell.current.col, coords.col);
          useProjectStore.getState().setProgram(updated);
        } else {
          const updated = drawLadderHLineSeries(currentProg, coords.netId, coords.row, coords.col, coords.col);
          useProjectStore.getState().setProgram(updated);
        }
        lastDrawnCell.current = coords;
      }
    } else if (selectionRect) {
      setSelectionRect(prev => prev ? { ...prev, currentX: e.clientX, currentY: e.clientY } : null);
    } else {
      const coords = getGridCoords(e.clientX, e.clientY);
      setHoverCell(coords || null);
    }
  };

  const handlePointerUp = (e: React.PointerEvent) => {
    if (isPanning) {
      setIsPanning(false);
      e.currentTarget.releasePointerCapture(e.pointerId);
      if (svgRef.current) svgRef.current.style.cursor = isSpacePressed.current ? 'grab' : 'default';
    }

    if (isDrawingLine.current) {
      isDrawingLine.current = false;
      lastDrawnCell.current = null;
      try {
        e.currentTarget.releasePointerCapture(e.pointerId);
      } catch (_) {}
      const currentProg = useProjectStore.getState().project.plc.program;
      if (strokeProgram.current && currentProg !== strokeProgram.current) {
        executeCommand(currentProg, strokeProgram.current);
      }
      strokeProgram.current = null;
      return;
    }
    
    if (selectionRect) {
      // Find all elements within the selection rect
      if (svgRef.current) {
        const svgRect = svgRef.current.getBoundingClientRect();
        const startX = Math.min(selectionRect.startX, selectionRect.currentX) - svgRect.left;
        const startY = Math.min(selectionRect.startY, selectionRect.currentY) - svgRect.top;
        const width = Math.abs(selectionRect.currentX - selectionRect.startX);
        const height = Math.abs(selectionRect.currentY - selectionRect.startY);
        
        // Transform screen rect to SVG coords
        const svgStartX = (startX - panX) / zoom;
        const svgStartY = (startY - panY) / zoom;
        const svgEndX = svgStartX + width / zoom;
        const svgEndY = svgStartY + height / zoom;
        
        const selectedIds: string[] = [];
        let currentY = 0;
        for (const net of program.networks) {
           const netRows = getNetRows(net);
           const netHeight = netRows * CELL_H;
           const yOffset = currentY + HEADER_H;
           
           for (const el of Object.values(net.elements)) {
             const isCoil = isRightAligned((el as any).type);
             const col = isCoil ? COLS - 1 : el.column;
             const elX = col * cellWidth + 40; // 40 is left padding
             const elY = el.row * CELL_H + yOffset;
             
             // Check if center of element is inside rect
             const cx = elX + cellWidth / 2;
             const cy = elY + CELL_H / 2;
             
             if (cx >= svgStartX && cx <= svgEndX && cy >= svgStartY && cy <= svgEndY) {
               selectedIds.push(el.id);
             }
           }
           currentY += netHeight + HEADER_H + NETWORK_GAP;
        }
        
        if (e.shiftKey || e.ctrlKey) {
           setSelectedElementIds([...new Set([...selectedElementIds, ...selectedIds])]);
        } else {
           setSelectedElementIds(selectedIds);
        }
        
        if (selectedIds.length === 1) {
          select(selectedIds[0]!, 'ladderElement');
        } else if (selectedIds.length > 1) {
          clearGlobalSelection(); // Multi-selection doesn't have a single properties panel yet
        }
      }
      setSelectionRect(null);
      e.currentTarget.releasePointerCapture(e.pointerId);
    }
    
    if (draggingElement) {
      if (hoverCell) {
        // Move element
        const { id, netId: sourceNetId } = draggingElement;
        const targetNetId = hoverCell.netId;
        
        let elToMove: LadderElement | undefined;
        let newNetworks = [...program.networks];
        
        // Find and remove from source
        newNetworks = newNetworks.map(n => {
          if (n.id === sourceNetId && n.elements[id]) {
             elToMove = n.elements[id];
             const { [id]: _, ...newEls } = n.elements;
             // Requirement 1: Replace non-wire source element with H_LINE
             if (elToMove.type !== 'H_LINE' && elToMove.type !== 'V_LINE') {
               const newLineId = `el_${Date.now()}_hline_${elToMove.row}_${elToMove.column}`;
               newEls[newLineId] = {
                 id: newLineId,
                 type: 'H_LINE',
                 row: elToMove.row,
                 column: elToMove.column
               };
             }
             return { ...n, elements: newEls };
          }
          return n;
        });
        
        if (elToMove) {
          const isCoil = isRightAligned((elToMove as any).type);
          let movedCol = isCoil ? COLS - 1 : hoverCell.col;
          const targetNetObj = newNetworks.find(n => n.id === targetNetId);
          const autoFillLines = targetNetObj
            ? getAutoFillLines(targetNetObj.elements, hoverCell.row, movedCol)
            : [];
          
          const movedEl = { ...elToMove, row: hoverCell.row, column: movedCol };
          newNetworks = newNetworks.map(n => {
            if (n.id === targetNetId) {
              let newEls = { ...n.elements };
              const toAdd = [movedEl, ...autoFillLines];
              toAdd.forEach(el => {
                 const isV = el.type === 'V_LINE';
                 Object.values(newEls).forEach(existing => {
                    if (existing.row === el.row && existing.column === el.column) {
                      const existingIsV = existing.type === 'V_LINE';
                      // Requirement 2: Delete underlying H_LINE or matching element
                      if (isV === existingIsV || existing.type === 'H_LINE' || (el.type !== 'V_LINE' && existing.type !== 'V_LINE')) {
                        delete newEls[existing.id];
                      }
                    }
                    if (['CTU', 'CTD', 'CTUD', 'SR', 'RS'].includes(el.type) && existing.row === el.row + 1 && existing.column === el.column && existing.type === 'H_LINE') {
                      delete newEls[existing.id];
                    }
                 });
                 newEls[el.id] = el;
              });
              return { ...n, elements: newEls };
            }
            return n;
          });
          
          const { networks: cleanedNetworks } = cleanOrphanedBranchLines(newNetworks);
          executeCommand({ ...program, networks: cleanedNetworks });
        }
      }
      setDraggingElement(null);
      setHoverCell(null);
    }
  };

  const handleWheel = (e: React.WheelEvent) => {
    if (e.ctrlKey) {
      e.preventDefault();
      const zoomFactor = e.deltaY > 0 ? 0.9 : 1.1;
      const newZoom = Math.max(0.2, Math.min(zoom * zoomFactor, 3));
      
      // Pointer-centered zoom
      if (svgRef.current) {
        const rect = svgRef.current.getBoundingClientRect();
        const pointerX = e.clientX - rect.left;
        const pointerY = e.clientY - rect.top;
        
        // The point (pointerX, pointerY) in current SVG coordinates:
        const svgX = (pointerX - panX) / zoom;
        const svgY = (pointerY - panY) / zoom;
        
        // We want the same point to be under the pointer after zoom
        const newPanX = pointerX - svgX * newZoom;
        const newPanY = Math.min(0, pointerY - svgY * newZoom);
        
        setZoom(newZoom);
        setPan(newPanX, newPanY);
      } else {
        setZoom(newZoom);
      }
    } else {
      setPan(panX - e.deltaX, Math.min(0, panY - e.deltaY));
    }
  };
  
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const preventDefault = (e: WheelEvent) => {
      if (e.ctrlKey) e.preventDefault();
    };
    el.addEventListener('wheel', preventDefault, { passive: false });
    const handleKeyDown = (e: KeyboardEvent) => {
      // Ignore shortcuts and deletion if user is typing in an input/textarea/select field
      if (isTextInputTarget(e.target) || (document.activeElement && ['INPUT', 'TEXTAREA', 'SELECT'].includes(document.activeElement.tagName))) {
        return;
      }

      // Ignore all editing, deletion, and selection shortcuts during simulation
      if (useSimulationStore.getState().snapshot.state !== 'STOPPED') {
        return;
      }

      // Ctrl+A Select All
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'a') {
        e.preventDefault();
        const allIds = program.networks.flatMap(n => Object.keys(n.elements));
        setSelectedElementIds(allIds);
        clearGlobalSelection();
        return;
      }
      
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'z') {
        if (e.shiftKey) {
          redo();
        } else {
          undo();
        }
        e.preventDefault();
      } else if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'y') {
        redo();
        e.preventDefault();
      } else if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'c') {
        const copiedEls: LadderElement[] = [];
        for (const net of program.networks) {
           for (const id of selectedElementIds) {
             if (net.elements[id]) copiedEls.push(net.elements[id]);
           }
        }
        setClipboard(copiedEls);
      } else if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'x') {
        const copiedEls: LadderElement[] = [];
        const newProg = {
          ...program,
          networks: program.networks.map(n => {
            let changed = false;
            for (const id of selectedElementIds) {
              if (n.elements[id]) {
                copiedEls.push(n.elements[id]);
                changed = true;
              }
            }
            if (changed) {
              const filtered = Object.fromEntries(Object.entries(n.elements).filter(([k]) => !selectedElementIds.includes(k)));
              return { ...n, elements: filtered };
            }
            return n;
          })
        };
        setClipboard(copiedEls);
        executeCommand(newProg);
        clearEditorSelection();
        clearGlobalSelection();
      } else if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'v') {
        if (clipboard.length > 0) {
          // Find target network and anchor
          let targetNetId = program.networks[0]?.id || '';
          if (!targetNetId) return;
          
          let rowOffset = 0;
          let colOffset = 0;
          
          if (hoverCell) {
            targetNetId = hoverCell.netId;
            const minRow = Math.min(...clipboard.map(e => e.row));
            const minCol = Math.min(...clipboard.map(e => e.column));
            rowOffset = hoverCell.row - minRow;
            colOffset = hoverCell.col - minCol;
          } else {
            // Paste offset slightly from original
            rowOffset = 1;
            colOffset = 1;
          }
          
          const newProg = { ...program };
          const newIds: string[] = [];
          
          newProg.networks = newProg.networks.map(n => {
            if (n.id === targetNetId) {
              const newEls = { ...n.elements };
              for (const el of clipboard) {
                const newId = `el_${Date.now()}_${Math.floor(Math.random()*1000)}`;
                newIds.push(newId);
                const placedRow = el.row + rowOffset;
                const placedCol = el.column + colOffset;
                // Delete underlying H_LINE or matching element
                Object.values(newEls).forEach(existing => {
                  if (existing.row === placedRow && existing.column === placedCol) {
                    if (existing.type === 'H_LINE' || (el.type !== 'V_LINE' && existing.type !== 'V_LINE')) {
                      delete newEls[existing.id];
                    }
                  }
                  if (['CTU', 'CTD', 'CTUD', 'SR', 'RS'].includes(el.type) && existing.row === placedRow + 1 && existing.column === placedCol && existing.type === 'H_LINE') {
                    delete newEls[existing.id];
                  }
                });
                newEls[newId] = {
                  ...el,
                  id: newId,
                  row: placedRow,
                  column: placedCol
                };
              }
              return { ...n, elements: newEls };
            }
            return n;
          });
          
          executeCommand(newProg);
          setSelectedElementIds(newIds);
          if (newIds.length === 1) {
             select(newIds[0]!, 'ladderElement');
          }
        }
      } else if (e.key === 'Delete' || e.key === 'Backspace') {
        e.preventDefault();
        handleDeleteKey(e.key as 'Delete' | 'Backspace');
      } else if (e.key === '-' || e.key === '_' || (!e.ctrlKey && !e.metaKey && !e.altKey && e.key.toLowerCase() === 'h')) {
        if (e.key.toLowerCase() === 'h' && selectedElementIds.length === 0) {
          e.preventDefault();
          setActiveTool(activeTool === 'H_LINE' ? null : 'H_LINE');
          return;
        }

        if (selectedElementIds.length === 1) {
          const selId = selectedElementIds[0]!;
          const prog = useProjectStore.getState().project.plc.program;
          let selEl: LadderElement | undefined;
          let selNet: LadderNetwork | undefined;
          for (const n of prog.networks) {
            if (n.elements[selId]) {
              selEl = n.elements[selId];
              selNet = n;
              break;
            }
          }

          if (selEl && selNet) {
            const nextCol = selEl.column + 1;
            if (nextCol < COLS) {
              e.preventDefault();
              const newLineId = `el_${Date.now()}_hline_${selEl.row}_${nextCol}`;
              let targetId = newLineId;
              const newProg = {
                ...prog,
                networks: prog.networks.map(n => {
                  if (n.id !== selNet!.id) return n;
                  const newEls = { ...n.elements };
                  const existing = Object.values(newEls).find(el => el.row === selEl!.row && el.column === nextCol && el.type !== 'V_LINE');
                  if (existing) {
                    targetId = existing.id;
                  } else {
                    newEls[newLineId] = {
                      id: newLineId,
                      type: 'H_LINE',
                      row: selEl!.row,
                      column: nextCol
                    };
                  }
                  return { ...n, elements: newEls };
                })
              };
              executeCommand(newProg);
              setSelectedElementIds([targetId]);
              select(targetId, 'ladderElement');
              return;
            }
          }
        }
      } else if (e.key === 'Escape') {
        if (activeTool) {
          e.preventDefault();
          setActiveTool(null);
          return;
        }
      }
    };
    window.addEventListener('keydown', handleKeyDown);

    return () => {
      el.removeEventListener('wheel', preventDefault);
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [undo, redo, executeCommand, program, selectedElementIds, clipboard, hoverCell, clearEditorSelection, clearGlobalSelection, setClipboard, setSelectedElementIds, select, activeTool, setActiveTool]);

  const getGridCoords = (clientX: number, clientY: number) => {
    if (!svgRef.current) return null;
    const rect = svgRef.current.getBoundingClientRect();
    const x = (clientX - rect.left - panX) / zoom;
    const y = (clientY - rect.top - panY) / zoom;
    
    // We stack networks vertically. 
    // Let's assume each network has a fixed number of rows for now to simplify, or we compute bounding boxes.
    // To keep things simple, let's just make one big SVG with networks drawn sequentially.
    let currentY = 0;
    for (const net of program.networks) {
      const netRows = getNetRows(net);
      const netHeight = netRows * CELL_H + HEADER_H;
      
      if (y >= currentY && y <= currentY + netHeight) {
        const localY = y - currentY - HEADER_H;
        if (localY >= 0) {
          const row = Math.floor(localY / CELL_H);
          let col = Math.floor((x - 40) / cellWidth); // 40px left rail margin
          if (x < 40) {
            col = 0;
          }
          if (col >= 0 && col < COLS && row >= 0 && row < netRows) {
            return { netId: net.id, row, col };
          }
        }
      }
      currentY += netHeight + NETWORK_GAP;
    }
    return null;
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    if (isSimulationRunning) {
      e.dataTransfer.dropEffect = 'none';
      return;
    }
    e.dataTransfer.dropEffect = 'copy';
    const coords = getGridCoords(e.clientX, e.clientY);
    if (coords) {
      setHoverCell(coords);
    } else {
      setHoverCell(null);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setHoverCell(null);
    if (isSimulationRunning) return;
    
    const type = e.dataTransfer.getData('application/ladder-element') as LadderElementType;
    if (!type) return;
    
    const coords = getGridCoords(e.clientX, e.clientY);
    if (!coords) return;
    
    placeElementAt(coords.netId, coords.row, coords.col, type);
  };

  let currentY = 0;

  return (
    <div 
      className={`${styles.container} ${activeTool === 'H_LINE' ? styles.containerDrawing : ''}`} 
      ref={containerRef}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
      onPointerLeave={handlePointerUp}
      onWheel={handleWheel}
      onDragOver={handleDragOver}
      onDrop={handleDrop}
      onContextMenu={(e) => {
        if (activeTool) {
          e.preventDefault();
          setActiveTool(null);
        }
      }}
      onClick={() => {
        if (activeTool) return;
        clearEditorSelection();
        clearGlobalSelection();
      }}
    >
      {activeTool && (
        <div className={styles.activeToolBanner}>
          <span className={styles.activeToolBadge}>
            {t('ladder.drawingMode')}: {activeTool === 'H_LINE' ? t('ladder.drawingMode.hLine') : activeTool}
          </span>
          <span className={styles.activeToolHint}>
            {activeTool === 'H_LINE' ? t('ladder.drawingMode.hLineHint') : t('ladder.drawingMode.elementHint')}
          </span>
          <button 
            className={styles.activeToolClose} 
            onClick={(e) => {
              e.stopPropagation();
              setActiveTool(null);
            }}
            title="Kapat (Esc)"
          >
            ✕
          </button>
        </div>
      )}
      <svg 
        ref={svgRef} 
        className={styles.svg} 
      >
        <g style={{ transform: `translate(${panX}px, ${panY}px) scale(${zoom})`, transformOrigin: '0 0' }}>
          <defs>
            <pattern id="grid" width={cellWidth} height={CELL_H} patternUnits="userSpaceOnUse">
              <path d={`M ${cellWidth} 0 L 0 0 0 ${CELL_H}`} fill="none" stroke="var(--color-border-strong, #3b3f46)" strokeWidth="1" strokeDasharray="4 4" />
            </pattern>
          </defs>

        {program.networks.map(net => {
          const netRows = getNetRows(net);
          const netHeight = netRows * CELL_H;
          const yOffset = currentY;
          currentY += netHeight + HEADER_H + NETWORK_GAP;

          return (
            <g key={net.id} transform={`translate(40, ${yOffset})`}>
              {/* Header */}
              <text x="0" y="20" fill="var(--color-text)" fontSize="14" fontWeight="bold">{net.name || net.id}</text>
              <text x="0" y="36" fill="var(--color-text-muted)" fontSize="12">{net.comment || 'No comment'}</text>
              
              <g transform={`translate(0, ${HEADER_H})`}>
                {/* Grid Background */}
                <rect x="0" y="0" width={netWidth} height={netHeight} fill="url(#grid)" />
                
                {/* Power Rails */}
                <line x1="0" y1="0" x2="0" y2={netHeight} stroke="red" strokeWidth="4" />
                <line x1={netWidth} y1="0" x2={netWidth} y2={netHeight} stroke="#0078d7" strokeWidth="4" />

                {/* Draw hovered cell */}
                {!isSimulationRunning && hoverCell && hoverCell.netId === net.id && (
                  <rect 
                    x={hoverCell.col * cellWidth} 
                    y={hoverCell.row * CELL_H} 
                    width={cellWidth} 
                    height={CELL_H} 
                    fill="var(--color-primary-alpha-20)" 
                    stroke="var(--color-primary)"
                    strokeWidth="2"
                  />
                )}

                {/* Elements */}
                {Object.values(net.elements).map((el) => {
                  const isDraggingThis = draggingElement?.id === el.id;
                  const isSelected = !isSimulationRunning && selectedElementIds.includes(el.id);
                  return (
                    <LadderElementNode 
                      key={el.id} 
                      element={el} 
                      selected={isSelected} 
                      activeTool={activeTool}
                      simState={simState.elementStates[el.id] as any}
                      timerState={simState.timers?.[(el as any).address]}
                      counterState={simState.counters?.[(el as any).address]}
                      cellWidth={cellWidth}
                      cols={COLS}
                      onSelect={(multi) => {
                        if (isSimulationRunning) return;
                        if (multi) {
                          toggleSelectedElementId(el.id);
                        } else {
                          setSelectedElementIds([el.id]);
                          select(el.id, 'ladderElement');
                        }
                      }} 
                      onPointerDown={(e) => {
                         if (isSimulationRunning) return;
                         if (activeTool) return;
                         
                         if (e.button === 0 && !isSpacePressed.current) {
                           e.stopPropagation();
                           
                           // If element is not already selected, select it
                           if (!selectedElementIds.includes(el.id)) {
                             if (!e.shiftKey && !e.ctrlKey) {
                               setSelectedElementIds([el.id]);
                               select(el.id, 'ladderElement');
                             } else {
                               toggleSelectedElementId(el.id);
                             }
                           }
                           
                           setDraggingElement({ id: el.id, netId: net.id });
                         }
                      }}
                      style={{ opacity: isDraggingThis ? 0.3 : 1 }}
                    />
                  );
                })}
              </g>
              {/* Network Actions */}
              {!isSimulationRunning && (
                <g 
                  transform={`translate(${netWidth + 16}, ${40 + netHeight / 2 - 12})`} 
                  style={{ cursor: 'pointer' }}
                  onClick={(e) => {
                    e.stopPropagation();
                    const newProg = {
                      ...program,
                      networks: program.networks.filter(n => n.id !== net.id)
                    };
                    executeCommand(newProg);
                  }}
                >
                  <rect width="24" height="24" fill="transparent" />
                  <path 
                    d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z" 
                    fill="var(--color-text-muted)" 
                    onMouseEnter={(e) => (e.currentTarget.style.fill = 'var(--color-error, #f44336)')}
                    onMouseLeave={(e) => (e.currentTarget.style.fill = 'var(--color-text-muted)')}
                    style={{ transition: 'fill 0.2s' }}
                  />
                </g>
              )}
            </g>
          );
        })}
        {/* Add Network Button */}
        {!isSimulationRunning && (
          <g transform={`translate(40, ${currentY + 20})`} style={{ cursor: 'pointer' }} onClick={(e) => {
            e.stopPropagation();
            const newNet: LadderNetwork = {
              id: `net_${Date.now()}`,
              name: `Network ${program.networks.length + 1}`,
              elements: {}
            };
            const newProg = { ...program, networks: [...program.networks, newNet] };
            executeCommand(newProg);
          }}>
            <rect width="120" height="32" rx="4" fill="var(--color-primary-alpha-20)" stroke="var(--color-primary)" />
            <text x="60" y="21" fill="var(--color-primary)" fontSize="14" fontWeight="bold" textAnchor="middle" style={{ pointerEvents: 'none' }}>+ Add Network</text>
          </g>
        )}
        {/* Selection Rectangle Overlay */}
        {selectionRect && (
          <rect
            x={(Math.min(selectionRect.startX, selectionRect.currentX) - (svgRef.current?.getBoundingClientRect().left || 0) - panX) / zoom}
            y={(Math.min(selectionRect.startY, selectionRect.currentY) - (svgRef.current?.getBoundingClientRect().top || 0) - panY) / zoom}
            width={Math.abs(selectionRect.currentX - selectionRect.startX) / zoom}
            height={Math.abs(selectionRect.currentY - selectionRect.startY) / zoom}
            fill="rgba(0, 120, 215, 0.2)"
            stroke="rgba(0, 120, 215, 0.8)"
            strokeWidth={1 / zoom}
            pointerEvents="none"
          />
        )}
        </g>
      </svg>
      
      {program.networks.length === 0 && (
        <div style={{
          position: 'absolute',
          inset: 0,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: 'rgba(30, 30, 30, 0.8)',
          zIndex: 10
        }}>
          <div style={{ color: 'var(--color-text-muted)', marginBottom: '16px', fontSize: '14px' }}>
            Boş Program (No Networks)
          </div>
          <button 
            style={{
              padding: '8px 16px',
              backgroundColor: 'var(--color-primary)',
              color: 'var(--color-on-primary)',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              fontWeight: 'bold'
            }}
            onClick={(e) => {
              e.stopPropagation();
              const newNet: LadderNetwork = {
                id: `net_${Date.now()}`,
                name: `Network 1`,
                elements: {}
              };
              const newProg = { ...program, networks: [...program.networks, newNet] };
              executeCommand(newProg);
              // Also try resetting pan to 0,0 when creating first network
              setPan(0, 0);
            }}
          >
            + Add First Network
          </button>
        </div>
      )}
    </div>
  );
}

function LadderElementNode({ 
  element, 
  selected,
  activeTool,
  simState,
  timerState,
  counterState,
  onSelect,
  onPointerDown,
  style,
  cellWidth: cellWidthProp,
  cols: colsProp
}: { 
  element: LadderElement; 
  selected: boolean; 
  activeTool?: string | null;
  simState?: { powerFlow: string, value?: boolean } | undefined;
  timerState?: { elapsedMs: number; presetMs: number; q: boolean; running: boolean; } | undefined;
  counterState?: { type: string; cv: number; pv: number; q: boolean; } | undefined;
  onSelect: (multi: boolean) => void;
  onPointerDown?: ((e: React.PointerEvent) => void) | undefined;
  style?: React.CSSProperties | undefined;
  cellWidth?: number;
  cols?: number;
}) {
  const CELL_W = cellWidthProp ?? 120;
  const COLS = colsProp ?? 12;

  // Coils always snap visually to the far right (COLS - 1)
  const isCoil = element.type === 'COIL' || element.type === 'INVERT_COIL' || element.type === 'SET_COIL' || element.type === 'RESET_COIL' || element.type === 'JMP' || element.type === 'LBL';
  const displayCol = isCoil ? COLS - 1 : element.column;
  
  const x = displayCol * CELL_W;
  const y = element.row * CELL_H;
  const cx = x + CELL_W / 2;
  const cy = y + CELL_H / 2;
  
  const hitX = x;
  const hitWidth = CELL_W;

  const isActive = simState?.powerFlow === 'ACTIVE';
  const color = isActive ? 'var(--color-success, #00ff00)' : 'white';
  const strokeWidth = isActive ? "3" : "2";

  // Basic rendering
  let content = <rect x={x+20} y={y+20} width={CELL_W-40} height={CELL_H-40} fill="magenta" />;
  
  switch (element.type) {
    case 'NO_CONTACT':
      content = (
        <g>
          <line x1={x} y1={cy} x2={cx - 10} y2={cy} stroke={color} strokeWidth={strokeWidth} />
          <line x1={cx + 10} y1={cy} x2={x + CELL_W} y2={cy} stroke={color} strokeWidth={strokeWidth} />
          <line x1={cx - 10} y1={cy - 15} x2={cx - 10} y2={cy + 15} stroke={color} strokeWidth={strokeWidth} />
          <line x1={cx + 10} y1={cy - 15} x2={cx + 10} y2={cy + 15} stroke={color} strokeWidth={strokeWidth} />
          <text x={cx} y={cy - 20} fill="yellow" fontSize="12" textAnchor="middle">{element.address}</text>
        </g>
      );
      break;
    case 'NC_CONTACT':
      content = (
        <g>
          <line x1={x} y1={cy} x2={cx - 10} y2={cy} stroke={color} strokeWidth={strokeWidth} />
          <line x1={cx + 10} y1={cy} x2={x + CELL_W} y2={cy} stroke={color} strokeWidth={strokeWidth} />
          <line x1={cx - 10} y1={cy - 15} x2={cx - 10} y2={cy + 15} stroke={color} strokeWidth={strokeWidth} />
          <line x1={cx + 10} y1={cy - 15} x2={cx + 10} y2={cy + 15} stroke={color} strokeWidth={strokeWidth} />
          <line x1={cx - 12} y1={cy + 12} x2={cx + 12} y2={cy - 12} stroke={color} strokeWidth={strokeWidth} />
          <text x={cx} y={cy - 20} fill="yellow" fontSize="12" textAnchor="middle">{element.address}</text>
        </g>
      );
      break;
    case 'P_CONTACT':
      content = (
        <g>
          <line x1={x} y1={cy} x2={cx - 10} y2={cy} stroke={color} strokeWidth={strokeWidth} />
          <line x1={cx + 10} y1={cy} x2={x + CELL_W} y2={cy} stroke={color} strokeWidth={strokeWidth} />
          <line x1={cx - 10} y1={cy - 15} x2={cx - 10} y2={cy + 15} stroke={color} strokeWidth={strokeWidth} />
          <line x1={cx + 10} y1={cy - 15} x2={cx + 10} y2={cy + 15} stroke={color} strokeWidth={strokeWidth} />
          <text x={cx} y={cy + 4} fill={color} fontSize="12" fontWeight="bold" textAnchor="middle">P</text>
          <text x={cx} y={cy - 20} fill="yellow" fontSize="12" textAnchor="middle">{element.address}</text>
        </g>
      );
      break;
    case 'N_CONTACT':
      content = (
        <g>
          <line x1={x} y1={cy} x2={cx - 10} y2={cy} stroke={color} strokeWidth={strokeWidth} />
          <line x1={cx + 10} y1={cy} x2={x + CELL_W} y2={cy} stroke={color} strokeWidth={strokeWidth} />
          <line x1={cx - 10} y1={cy - 15} x2={cx - 10} y2={cy + 15} stroke={color} strokeWidth={strokeWidth} />
          <line x1={cx + 10} y1={cy - 15} x2={cx + 10} y2={cy + 15} stroke={color} strokeWidth={strokeWidth} />
          <text x={cx} y={cy + 4} fill={color} fontSize="12" fontWeight="bold" textAnchor="middle">N</text>
          <text x={cx} y={cy - 20} fill="yellow" fontSize="12" textAnchor="middle">{element.address}</text>
        </g>
      );
      break;
    case 'NOT_CONTACT':
      content = (
        <g>
          <line x1={x} y1={cy} x2={cx - 14} y2={cy} stroke={color} strokeWidth={strokeWidth} />
          <line x1={cx + 14} y1={cy} x2={x + CELL_W} y2={cy} stroke={color} strokeWidth={strokeWidth} />
          <rect x={cx - 14} y={cy - 12} width={28} height={24} fill="#1e293b" stroke={color} strokeWidth={strokeWidth} rx="3" />
          <text x={cx} y={cy + 4} fill="white" fontSize="9" fontWeight="bold" textAnchor="middle">NOT</text>
        </g>
      );
      break;
    case 'COIL':
    case 'SET_COIL':
    case 'RESET_COIL':
    case 'INVERT_COIL':
      content = (
        <g>
          {/* Wire to the left */}
          <line x1={x} y1={cy} x2={cx - 15} y2={cy} stroke={color} strokeWidth={strokeWidth} />
          {/* Wire to the right rail */}
          <line x1={cx + 15} y1={cy} x2={COLS * CELL_W} y2={cy} stroke={color} strokeWidth={strokeWidth} />
          <path d={`M ${cx - 5} ${cy - 15} Q ${cx - 15} ${cy} ${cx - 5} ${cy + 15}`} fill="none" stroke={color} strokeWidth={strokeWidth} />
          <path d={`M ${cx + 5} ${cy - 15} Q ${cx + 15} ${cy} ${cx + 5} ${cy + 15}`} fill="none" stroke={color} strokeWidth={strokeWidth} />
          <text x={cx} y={cy - 25} fill="yellow" fontSize="12" textAnchor="middle">{element.address}</text>
          {element.type === 'SET_COIL' && <text x={cx} y={cy + 4} fill={color} fontSize="12" textAnchor="middle">S</text>}
          {element.type === 'RESET_COIL' && <text x={cx} y={cy + 4} fill={color} fontSize="12" textAnchor="middle">R</text>}
          {element.type === 'INVERT_COIL' && (
            <line x1={cx - 8} y1={cy + 10} x2={cx + 8} y2={cy - 10} stroke={color} strokeWidth={strokeWidth} />
          )}
        </g>
      );
      break;
    case 'JMP':
    case 'LBL':
      content = (
        <g>
          <line x1={x} y1={cy} x2={cx - 15} y2={cy} stroke={color} strokeWidth={strokeWidth} />
          <line x1={cx + 15} y1={cy} x2={COLS * CELL_W} y2={cy} stroke={color} strokeWidth={strokeWidth} />
          <path d={`M ${cx - 5} ${cy - 15} Q ${cx - 15} ${cy} ${cx - 5} ${cy + 15}`} fill="none" stroke={color} strokeWidth={strokeWidth} />
          <path d={`M ${cx + 5} ${cy - 15} Q ${cx + 15} ${cy} ${cx + 5} ${cy + 15}`} fill="none" stroke={color} strokeWidth={strokeWidth} />
          <text x={cx} y={cy - 25} fill="yellow" fontSize="12" textAnchor="middle">{element.type}</text>
          <text x={cx} y={cy + 4} fill="cyan" fontSize="11" fontWeight="bold" textAnchor="middle">{(element as any).label || 'LABEL'}</text>
        </g>
      );
      break;
    case 'CMP_EQ_I':
    case 'CMP_NE_I':
    case 'CMP_GT_I':
    case 'CMP_LT_I':
    case 'CMP_GE_I':
    case 'CMP_LE_I': {
      const op = element.type.replace('CMP_', '').replace('_I', '');
      const symbol = op === 'EQ' ? '==' : op === 'NE' ? '<>' : op === 'GT' ? '>' : op === 'LT' ? '<' : op === 'GE' ? '>=' : '<=';
      content = (
        <g>
          {/* Power flow connection lines */}
          <line x1={x} y1={cy} x2={x + 10} y2={cy} stroke={color} strokeWidth={strokeWidth} />
          <line x1={x + CELL_W - 10} y1={cy} x2={x + CELL_W} y2={cy} stroke={color} strokeWidth={strokeWidth} />
          
          {/* Block body */}
          <rect x={x + 10} y={y + 8} width={CELL_W - 20} height={CELL_H - 16} fill="#1e293b" stroke={color} strokeWidth="2" rx="4" />
          
          {/* Center operator band aligned directly with the power flow line */}
          <rect x={x + 10} y={cy - 10} width={CELL_W - 20} height={20} fill="rgba(255, 255, 255, 0.05)" />
          <line x1={x + 10} y1={cy - 10} x2={x + CELL_W - 10} y2={cy - 10} stroke="rgba(255, 255, 255, 0.12)" strokeWidth="1" strokeDasharray="2,2" />
          <line x1={x + 10} y1={cy + 10} x2={x + CELL_W - 10} y2={cy + 10} stroke="rgba(255, 255, 255, 0.12)" strokeWidth="1" strokeDasharray="2,2" />
          <text x={cx} y={cy + 4} fill="white" fontSize="13" fontWeight="bold" textAnchor="middle">{symbol} I</text>
          
          {/* IN1: Top operand */}
          <text x={x + 16} y={cy - 16} fill="#94a3b8" fontSize="8" fontWeight="bold">IN1</text>
          <text x={cx + 8} y={cy - 16} fill="#38bdf8" fontSize="11" fontWeight="bold" textAnchor="middle">{(element as any).in1 ?? '0'}</text>
          
          {/* IN2: Bottom operand */}
          <text x={x + 16} y={cy + 22} fill="#94a3b8" fontSize="8" fontWeight="bold">IN2</text>
          <text x={cx + 8} y={cy + 22} fill="#38bdf8" fontSize="11" fontWeight="bold" textAnchor="middle">{(element as any).in2 ?? '0'}</text>
        </g>
      );
      break;
    }
    case 'CMP_EQ_R':
    case 'CMP_NE_R':
    case 'CMP_GT_R':
    case 'CMP_LT_R':
    case 'CMP_GE_R':
    case 'CMP_LE_R': {
      const op = element.type.replace('CMP_', '').replace('_R', '');
      const symbol = op === 'EQ' ? '==' : op === 'NE' ? '<>' : op === 'GT' ? '>' : op === 'LT' ? '<' : op === 'GE' ? '>=' : '<=';
      content = (
        <g>
          {/* Power flow connection lines */}
          <line x1={x} y1={cy} x2={x + 10} y2={cy} stroke={color} strokeWidth={strokeWidth} />
          <line x1={x + CELL_W - 10} y1={cy} x2={x + CELL_W} y2={cy} stroke={color} strokeWidth={strokeWidth} />
          
          {/* Block body */}
          <rect x={x + 10} y={y + 8} width={CELL_W - 20} height={CELL_H - 16} fill="#1e293b" stroke={color} strokeWidth="2" rx="4" />
          
          {/* Center operator band aligned directly with the power flow line */}
          <rect x={x + 10} y={cy - 10} width={CELL_W - 20} height={20} fill="rgba(56, 189, 248, 0.08)" />
          <line x1={x + 10} y1={cy - 10} x2={x + CELL_W - 10} y2={cy - 10} stroke="rgba(255, 255, 255, 0.12)" strokeWidth="1" strokeDasharray="2,2" />
          <line x1={x + 10} y1={cy + 10} x2={x + CELL_W - 10} y2={cy + 10} stroke="rgba(255, 255, 255, 0.12)" strokeWidth="1" strokeDasharray="2,2" />
          <text x={cx} y={cy + 4} fill="#38bdf8" fontSize="13" fontWeight="bold" textAnchor="middle">{symbol} R</text>
          
          {/* IN1: Top operand */}
          <text x={x + 16} y={cy - 16} fill="#94a3b8" fontSize="8" fontWeight="bold">IN1</text>
          <text x={cx + 8} y={cy - 16} fill="#38bdf8" fontSize="11" fontWeight="bold" textAnchor="middle">{(element as any).in1 ?? '0.0'}</text>
          
          {/* IN2: Bottom operand */}
          <text x={x + 16} y={cy + 22} fill="#94a3b8" fontSize="8" fontWeight="bold">IN2</text>
          <text x={cx + 8} y={cy + 22} fill="#38bdf8" fontSize="11" fontWeight="bold" textAnchor="middle">{(element as any).in2 ?? '0.0'}</text>
        </g>
      );
      break;
    }
    case 'ADD_I':
    case 'SUB_I':
    case 'MUL_I':
    case 'DIV_I': {
      const op = element.type;
      content = (
        <g>
          <line x1={x} y1={cy} x2={x + 10} y2={cy} stroke={color} strokeWidth={strokeWidth} />
          <line x1={x + CELL_W - 10} y1={cy} x2={x + CELL_W} y2={cy} stroke={color} strokeWidth={strokeWidth} />
          <rect x={x + 10} y={y + 5} width={CELL_W - 20} height={CELL_H - 10} fill="#333" stroke={color} strokeWidth="2" rx="4" />
          <text x={cx} y={y + 20} fill="white" fontSize="12" fontWeight="bold" textAnchor="middle">{op}</text>
          
          <text x={x + 15} y={cy} fill="yellow" fontSize="9">IN1</text>
          <text x={x + 15} y={cy + 12} fill="cyan" fontSize="10">{(element as any).in1}</text>
          
          <text x={x + 15} y={cy + 24} fill="yellow" fontSize="9">IN2</text>
          <text x={x + 15} y={cy + 36} fill="cyan" fontSize="10">{(element as any).in2}</text>

          <text x={x + CELL_W - 15} y={cy + 24} fill="yellow" fontSize="9" textAnchor="end">OUT</text>
          <text x={x + CELL_W - 15} y={cy + 36} fill="magenta" fontSize="10" textAnchor="end">{(element as any).out}</text>
        </g>
      );
      break;
    }
    case 'ADD_R':
    case 'SUB_R':
    case 'MUL_R':
    case 'DIV_R': {
      const op = element.type;
      content = (
        <g>
          <line x1={x} y1={cy} x2={x + 10} y2={cy} stroke={color} strokeWidth={strokeWidth} />
          <line x1={x + CELL_W - 10} y1={cy} x2={x + CELL_W} y2={cy} stroke={color} strokeWidth={strokeWidth} />
          <rect x={x + 10} y={y + 5} width={CELL_W - 20} height={CELL_H - 10} fill="#1e293b" stroke={color} strokeWidth="2" rx="4" />
          <text x={cx} y={y + 20} fill="#38bdf8" fontSize="12" fontWeight="bold" textAnchor="middle">{op}</text>
          
          <text x={x + 15} y={cy} fill="yellow" fontSize="9">IN1</text>
          <text x={x + 15} y={cy + 12} fill="cyan" fontSize="10">{(element as any).in1}</text>
          
          <text x={x + 15} y={cy + 24} fill="yellow" fontSize="9">IN2</text>
          <text x={x + 15} y={cy + 36} fill="cyan" fontSize="10">{(element as any).in2}</text>

          <text x={x + CELL_W - 15} y={cy + 24} fill="yellow" fontSize="9" textAnchor="end">OUT</text>
          <text x={x + CELL_W - 15} y={cy + 36} fill="magenta" fontSize="10" textAnchor="end">{(element as any).out}</text>
        </g>
      );
      break;
    }
    case 'MOV_W':
    case 'MOV_R': {
      const op = element.type;
      content = (
        <g>
          <line x1={x} y1={cy} x2={x + 10} y2={cy} stroke={color} strokeWidth={strokeWidth} />
          <line x1={x + CELL_W - 10} y1={cy} x2={x + CELL_W} y2={cy} stroke={color} strokeWidth={strokeWidth} />
          <rect x={x + 10} y={y + 5} width={CELL_W - 20} height={CELL_H - 10} fill="#333" stroke={color} strokeWidth="2" rx="4" />
          <text x={cx} y={y + 20} fill="white" fontSize="12" fontWeight="bold" textAnchor="middle">{op}</text>
          
          <text x={x + 15} y={cy + 10} fill="yellow" fontSize="9">IN</text>
          <text x={x + 15} y={cy + 22} fill="cyan" fontSize="10">{(element as any).in}</text>

          <text x={x + CELL_W - 15} y={cy + 10} fill="yellow" fontSize="9" textAnchor="end">OUT</text>
          <text x={x + CELL_W - 15} y={cy + 22} fill="magenta" fontSize="10" textAnchor="end">{(element as any).out}</text>
        </g>
      );
      break;
    }
    case 'SCALE': {
      content = (
        <g>
          <line x1={x} y1={cy} x2={x + 10} y2={cy} stroke={color} strokeWidth={strokeWidth} />
          <line x1={x + CELL_W - 10} y1={cy} x2={x + CELL_W} y2={cy} stroke={color} strokeWidth={strokeWidth} />
          <rect x={x + 6} y={y + 4} width={CELL_W - 12} height={CELL_H - 8} fill="#1e293b" stroke={color} strokeWidth="2" rx="4" />
          <text x={cx} y={y + 18} fill="#38bdf8" fontSize="11" fontWeight="bold" textAnchor="middle">SCALE</text>
          
          <text x={x + 12} y={cy - 4} fill="yellow" fontSize="9">IN: {(element as any).in}</text>
          <text x={x + 12} y={cy + 8} fill="#94a3b8" fontSize="8">[{ (element as any).inMin }..{ (element as any).inMax }]</text>
          <text x={x + 12} y={cy + 20} fill="#a78bfa" fontSize="8">[{ (element as any).outMin }..{ (element as any).outMax }]</text>

          <text x={x + CELL_W - 12} y={cy + 20} fill="magenta" fontSize="9" fontWeight="bold" textAnchor="end">{(element as any).out}</text>
        </g>
      );
      break;
    }
    case 'IN_RANGE': {
      content = (
        <g>
          <line x1={x} y1={cy} x2={x + 10} y2={cy} stroke={color} strokeWidth={strokeWidth} />
          <line x1={x + CELL_W - 10} y1={cy} x2={x + CELL_W} y2={cy} stroke={color} strokeWidth={strokeWidth} />
          <rect x={x + 8} y={y + 8} width={CELL_W - 16} height={CELL_H - 16} fill="#1e293b" stroke={color} strokeWidth="2" rx="4" />
          <text x={cx} y={y + 24} fill="#38bdf8" fontSize="10" fontWeight="bold" textAnchor="middle">IN_RANGE</text>
          <text x={cx} y={cy + 4} fill="cyan" fontSize="11" fontWeight="bold" textAnchor="middle">{(element as any).in}</text>
          <text x={x + 14} y={cy + 22} fill="#94a3b8" fontSize="9">{(element as any).min}</text>
          <text x={x + CELL_W - 14} y={cy + 22} fill="#94a3b8" fontSize="9" textAnchor="end">{(element as any).max}</text>
        </g>
      );
      break;
    }
    case 'SR':
    case 'RS': {
      const addr = (element as any).address;
      const height = CELL_H * 2;
      const cy1 = y + CELL_H / 2;
      const cy2 = y + CELL_H + CELL_H / 2;
      const isSR = element.type === 'SR';
      
      content = (
        <g>
          {/* Top input */}
          <line x1={x} y1={cy1} x2={x + 10} y2={cy1} stroke={color} strokeWidth={strokeWidth} />
          {/* Bottom input */}
          <line x1={x} y1={cy2} x2={x + 10} y2={cy2} stroke={color} strokeWidth={strokeWidth} />
          
          {/* Output */}
          <line x1={x + CELL_W - 10} y1={cy1} x2={x + CELL_W} y2={cy1} stroke={color} strokeWidth={strokeWidth} />
          
          <rect x={x + 10} y={y + 10} width={CELL_W - 20} height={height - 20} fill="var(--color-bg-base)" stroke={color} strokeWidth={strokeWidth} rx="4" />
          
          <text x={cx} y={y + 28} fill="white" fontSize="14" fontWeight="bold" textAnchor="middle">{element.type}</text>
          <text x={cx} y={y + 50} fill="yellow" fontSize="12" textAnchor="middle">{addr}</text>
          
          <text x={cx} y={y + 75} fill="var(--color-text-muted)" fontSize="9" textAnchor="middle">{isSR ? 'Set Dominant' : 'Reset Dominant'}</text>

          {/* Labels for pins */}
          <text x={x + 15} y={cy1 + 4} fill="white" fontSize="11" fontWeight="bold">S</text>
          <text x={x + 15} y={cy2 + 4} fill="white" fontSize="11" fontWeight="bold">{isSR ? 'R' : 'R1'}</text>
          <text x={x + CELL_W - 15} y={cy1 + 4} fill="white" fontSize="11" fontWeight="bold" textAnchor="end">Q</text>
        </g>
      );
      break;
    }
    case 'TONR': {
      const pTime = (element as any).presetTime;
      const addr = (element as any).address;
      const tState = timerState;
      const displayEt = tState ? formatTime(tState.elapsedMs) : '0ms';
      const isQ = tState?.q ? true : false;
      const height = CELL_H * 2;
      const cy1 = y + CELL_H / 2;
      const cy2 = y + CELL_H + CELL_H / 2;

      content = (
        <g>
          {/* Top input: IN */}
          <line x1={x} y1={cy1} x2={x + 10} y2={cy1} stroke={color} strokeWidth={strokeWidth} />
          {/* Bottom input: R */}
          <line x1={x} y1={cy2} x2={x + 10} y2={cy2} stroke={color} strokeWidth={strokeWidth} />
          
          {/* Output Q */}
          <line x1={x + CELL_W - 10} y1={cy1} x2={x + CELL_W} y2={cy1} stroke={isQ ? 'var(--color-success, #00ff00)' : 'white'} strokeWidth={isQ ? "3" : "2"} />
          
          <rect x={x + 10} y={y + 10} width={CELL_W - 20} height={height - 20} fill="var(--color-bg-base)" stroke={color} strokeWidth={strokeWidth} rx="4" />
          
          <text x={cx} y={y + 26} fill="white" fontSize="13" fontWeight="bold" textAnchor="middle">TONR</text>
          <text x={cx} y={y + 44} fill="yellow" fontSize="12" textAnchor="middle">{addr}</text>
          <text x={cx} y={y + 64} fill="var(--color-text-muted)" fontSize="10" textAnchor="middle">PT: {pTime}</text>
          {tState && <text x={cx} y={y + 80} fill="var(--color-primary)" fontSize="10" textAnchor="middle">ET: {displayEt}</text>}

          {/* Pin labels */}
          <text x={x + 15} y={cy1 + 4} fill="white" fontSize="10" fontWeight="bold">IN</text>
          <text x={x + 15} y={cy2 + 4} fill="white" fontSize="10" fontWeight="bold">R</text>
          <text x={x + CELL_W - 15} y={cy1 + 4} fill="white" fontSize="10" fontWeight="bold" textAnchor="end">Q</text>
        </g>
      );
      break;
    }
    case 'H_LINE':
      content = <line x1={x} y1={cy} x2={x + CELL_W} y2={cy} stroke={color} strokeWidth={strokeWidth} />;
      break;
    case 'V_LINE':
      content = (
        <g>
          <line x1={x} y1={cy} x2={x} y2={cy + CELL_H} stroke={color} strokeWidth={strokeWidth} />
        </g>
      );
      break;
    case 'TON':
    case 'TOF':
    case 'TP': {
      const pTime = (element as any).presetTime;
      const addr = (element as any).address;
      const tState = timerState;
      const displayEt = tState ? formatTime(tState.elapsedMs) : '0ms';
      const isQ = tState?.q ? true : false;
      
      content = (
        <g>
          <line x1={x} y1={cy} x2={x + 10} y2={cy} stroke={color} strokeWidth={strokeWidth} />
          <line x1={x + CELL_W - 10} y1={cy} x2={x + CELL_W} y2={cy} stroke={isQ ? 'var(--color-success, #00ff00)' : 'white'} strokeWidth={isQ ? "3" : "2"} />
          <rect x={x + 10} y={y + 10} width={CELL_W - 20} height={CELL_H - 20} fill="var(--color-bg-base)" stroke={color} strokeWidth={strokeWidth} rx="4" />
          <text x={cx} y={y + 25} fill="white" fontSize="14" fontWeight="bold" textAnchor="middle">{element.type}</text>
          <text x={cx} y={y + 40} fill="yellow" fontSize="12" textAnchor="middle">{addr}</text>
          <text x={cx} y={y + 55} fill="var(--color-text-muted)" fontSize="10" textAnchor="middle">PT: {pTime}</text>
          {tState && <text x={cx} y={y + 67} fill="var(--color-primary)" fontSize="10" textAnchor="middle">ET: {displayEt}</text>}
        </g>
      );
      break;
    }
    case 'CTU':
    case 'CTD':
    case 'CTUD': {
      const pv = (element as any).presetValue;
      const addr = (element as any).address;
      const cState = counterState;
      const displayCv = cState ? cState.cv : 0;
      const isQ = cState?.q ? true : false;
      const height = CELL_H * 2; // Spans 2 rows!
      const cy1 = y + CELL_H / 2;
      const cy2 = y + CELL_H + CELL_H / 2;
      
      content = (
        <g>
          {/* Top input */}
          <line x1={x} y1={cy1} x2={x + 10} y2={cy1} stroke={color} strokeWidth={strokeWidth} />
          {/* Bottom input */}
          <line x1={x} y1={cy2} x2={x + 10} y2={cy2} stroke={color} strokeWidth={strokeWidth} />
          
          {/* Output */}
          <line x1={x + CELL_W - 10} y1={cy1} x2={x + CELL_W} y2={cy1} stroke={isQ ? 'var(--color-success, #00ff00)' : 'white'} strokeWidth={isQ ? "3" : "2"} />
          
          <rect x={x + 10} y={y + 10} width={CELL_W - 20} height={height - 20} fill="var(--color-bg-base)" stroke={color} strokeWidth={strokeWidth} rx="4" />
          
          <text x={cx} y={y + 25} fill="white" fontSize="14" fontWeight="bold" textAnchor="middle">{element.type}</text>
          <text x={cx} y={y + 45} fill="yellow" fontSize="12" textAnchor="middle">{addr}</text>
          
          <text x={cx} y={y + 70} fill="var(--color-text-muted)" fontSize="10" textAnchor="middle">PV: {pv}</text>
          <text x={cx} y={y + 90} fill="var(--color-primary)" fontSize="10" textAnchor="middle">CV: {displayCv}</text>

          {/* Labels for pins */}
          <text x={x + 15} y={cy1 + 4} fill="white" fontSize="10">{element.type === 'CTUD' ? 'CU' : element.type === 'CTD' ? 'CD' : 'CU'}</text>
          <text x={x + 15} y={cy2 + 4} fill="white" fontSize="10">{element.type === 'CTUD' ? 'CD' : element.type === 'CTU' ? 'R' : 'LD'}</text>
        </g>
      );
      break;
    }
    default:
      content = <rect x={x+20} y={y+20} width={CELL_W-40} height={CELL_H-40} fill="magenta" />;
  }


  return (
    <g 
      data-element-id={element.id} 
      style={{ cursor: 'pointer', ...style }}
      onClick={(e) => { 
        if (activeTool) return;
        e.stopPropagation(); 
        const simState = useSimulationStore.getState().snapshot.state;
        if (simState !== 'STOPPED') {
          if ((element.type === 'NO_CONTACT' || element.type === 'NC_CONTACT') && element.address && simState === 'RUNNING') {
            const addrStr = element.address;
            if (addrStr.startsWith('I') || addrStr.startsWith('PI')) {
              const parsed = parseAddress(addrStr);
              if (parsed && parsed.area === 'I') {
                const currentVal = useSimulationStore.getState().engine.host.pi.inputs.getBit(parsed.byteOffset, parsed.bitOffset);
                useSimulationStore.getState().setInput(parsed.byteOffset, parsed.bitOffset, !currentVal);
              }
            }
          }
          return; // Never allow selection during simulation
        }
        onSelect(e.ctrlKey || e.shiftKey); 
      }}
      onPointerDown={onPointerDown}
    >
      {/* Invisible rect to increase clickable area */}
      {element.type === 'V_LINE' ? (
        <rect x={x - 10} y={y} width={20} height={CELL_H} fill="transparent" />
      ) : (
        <rect x={hitX} y={y} width={hitWidth} height={CELL_H} fill="transparent" />
      )}
      
      {selected && (
        <rect x={hitX} y={y} width={hitWidth} height={CELL_H} fill="none" stroke="var(--color-primary)" strokeWidth="2" strokeDasharray="4" />
      )}
      {content}
    </g>
  );
}
